import React, { useState, useMemo } from 'react';
import { PatternRecognition } from '../engines/PatternRecognition.js';

function PatternsTab({ expirations }) {
  const [selectedPattern, setSelectedPattern] = useState('all');
  
  // Detect all patterns
  const patterns = useMemo(() => {
    const engine = new PatternRecognition(expirations);
    const allPatterns = engine.detectAllPatterns();
    const prediction = engine.generatePrediction(allPatterns);
    
    return { ...allPatterns, prediction };
  }, [expirations]);

  const filteredPatterns = useMemo(() => {
    if (selectedPattern === 'all') return patterns;
    
    const filtered = { ...patterns };
    Object.keys(filtered).forEach(key => {
      if (key !== selectedPattern && key !== 'prediction') {
        filtered[key] = [];
      }
    });
    return filtered;
  }, [patterns, selectedPattern]);

  return (
    <div className="patterns-tab">
      <div className="patterns-header">
        <h2>🔍 זיהוי דפוסים - Pattern Recognition</h2>
        <p className="tab-description">
          מנוע ML מזהה דפוסים טכניים ומגמות בנתונים ההיסטוריים
        </p>
      </div>

      {/* ML Prediction */}
      <div className="ml-prediction-section">
        <h3>🤖 חיזוי ML</h3>
        <div className="prediction-container">
          <div className="prediction-main">
            <div className="prediction-direction">
              <span className="direction-label">כיוון צפוי:</span>
              <span className={`direction-value ${
                patterns.prediction.direction === 'עולה' ? 'bullish' : 
                patterns.prediction.direction === 'יורד' ? 'bearish' : 'neutral'
              }`}>
                {patterns.prediction.direction === 'עולה' ? '📈' : 
                 patterns.prediction.direction === 'יורד' ? '📉' : '↔️'} {patterns.prediction.direction}
              </span>
            </div>
            
            <div className="confidence-bar">
              <div className="confidence-label">רמת ביטחון: {(patterns.prediction.confidence * 100).toFixed(0)}%</div>
              <div className="confidence-track">
                <div 
                  className="confidence-fill"
                  style={{ width: `${patterns.prediction.confidence * 100}%` }}
                />
              </div>
            </div>

            <div className="probability-grid">
              <div className="probability-item bullish">
                <div className="prob-label">הסתברות עלייה</div>
                <div className="prob-value">{patterns.prediction.bullishProbability}%</div>
              </div>
              <div className="probability-item bearish">
                <div className="prob-label">הסתברות ירידה</div>
                <div className="prob-value">{patterns.prediction.bearishProbability}%</div>
              </div>
            </div>
          </div>

          <div className="prediction-signals">
            <h4>🎯 סיגנלים שזוהו:</h4>
            <ul>
              {patterns.prediction.signals.map((signal, index) => (
                <li key={index}>{signal}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Pattern Filter */}
      <div className="pattern-filter-section">
        <label>סינון דפוסים:</label>
        <select 
          value={selectedPattern}
          onChange={(e) => setSelectedPattern(e.target.value)}
        >
          <option value="all">כל הדפוסים</option>
          <option value="consecutiveMovements">תנועות רצופות</option>
          <option value="supportResistance">תמיכה והתנגדות</option>
          <option value="volatilityClusters">אשכולות תנודתיות</option>
          <option value="trendStrength">חוזק מגמה</option>
          <option value="meanReversion">חזרה לממוצע</option>
          <option value="breakouts">פריצות</option>
        </select>
      </div>

      {/* Consecutive Movements */}
      {(selectedPattern === 'all' || selectedPattern === 'consecutiveMovements') && 
       filteredPatterns.consecutiveMovements.length > 0 && (
        <div className="pattern-section">
          <h3>🔄 תנועות רצופות (Streaks)</h3>
          <div className="patterns-grid">
            {filteredPatterns.consecutiveMovements.slice(0, 8).map((pattern, index) => (
              <div key={index} className={`pattern-card ${pattern.direction}`}>
                <div className="pattern-icon">
                  {pattern.direction === 'up' ? '📈' : '📉'}
                </div>
                <div className="pattern-content">
                  <div className="pattern-title">
                    {pattern.count} תנועות {pattern.direction === 'up' ? 'עולות' : 'יורדות'}
                  </div>
                  <div className="pattern-dates">
                    {pattern.startDate} - {pattern.endDate}
                  </div>
                  <div className={`pattern-significance ${pattern.significance}`}>
                    {pattern.significance === 'high' ? '⚠️ חשיבות גבוהה' : '⚡ חשיבות בינונית'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Support & Resistance */}
      {(selectedPattern === 'all' || selectedPattern === 'supportResistance') && 
       filteredPatterns.supportResistance.length > 0 && (
        <div className="pattern-section">
          <h3>🎯 תמיכה והתנגדות</h3>
          <div className="sr-levels-container">
            <div className="sr-column">
              <h4>🟢 רמות תמיכה</h4>
              {filteredPatterns.supportResistance
                .filter(l => l.type === 'support')
                .slice(0, 5)
                .map((level, index) => (
                  <div key={index} className="sr-level support">
                    <div className="level-price">{level.level.toFixed(1)}</div>
                    <div className="level-info">
                      <span>{level.touches} נגיעות</span>
                      <span className={`strength-badge ${level.strength}`}>
                        {level.strength === 'strong' ? '💪 חזק' : '👍 בינוני'}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
            
            <div className="sr-column">
              <h4>🔴 רמות התנגדות</h4>
              {filteredPatterns.supportResistance
                .filter(l => l.type === 'resistance')
                .slice(0, 5)
                .map((level, index) => (
                  <div key={index} className="sr-level resistance">
                    <div className="level-price">{level.level.toFixed(1)}</div>
                    <div className="level-info">
                      <span>{level.touches} נגיעות</span>
                      <span className={`strength-badge ${level.strength}`}>
                        {level.strength === 'strong' ? '💪 חזק' : '👍 בינוני'}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Volatility Clusters */}
      {(selectedPattern === 'all' || selectedPattern === 'volatilityClusters') && 
       filteredPatterns.volatilityClusters.length > 0 && (
        <div className="pattern-section">
          <h3>⚡ אשכולות תנודתיות</h3>
          <div className="patterns-grid">
            {filteredPatterns.volatilityClusters.slice(0, 6).map((cluster, index) => (
              <div key={index} className="pattern-card volatility">
                <div className="pattern-icon">⚡</div>
                <div className="pattern-content">
                  <div className="pattern-title">
                    {cluster.duration} ימי תנודתיות
                  </div>
                  <div className="pattern-dates">
                    {cluster.startDate} - {cluster.endDate}
                  </div>
                  <div className="pattern-detail">
                    ממוצע: {cluster.avgVolatility.toFixed(2)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trend Strength */}
      {(selectedPattern === 'all' || selectedPattern === 'trendStrength') && 
       filteredPatterns.trendStrength.length > 0 && (
        <div className="pattern-section">
          <h3>💪 חוזק מגמה</h3>
          <div className="patterns-grid">
            {filteredPatterns.trendStrength.slice(0, 8).map((trend, index) => (
              <div key={index} className={`pattern-card ${trend.direction}`}>
                <div className="pattern-icon">
                  {trend.direction === 'bullish' ? '🐂' : '🐻'}
                </div>
                <div className="pattern-content">
                  <div className="pattern-title">
                    מגמה {trend.direction === 'bullish' ? 'עולה' : 'יורדת'}
                  </div>
                  <div className="pattern-detail">
                    חוזק: {trend.strength}
                  </div>
                  <div className="pattern-dates">{trend.date}</div>
                  <div className={`pattern-confidence ${trend.confidence}`}>
                    {trend.confidence === 'high' ? '🎯 ביטחון גבוה' : '👍 ביטחון בינוני'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mean Reversion */}
      {(selectedPattern === 'all' || selectedPattern === 'meanReversion') && 
       filteredPatterns.meanReversion.length > 0 && (
        <div className="pattern-section">
          <h3>🔄 הזדמנויות חזרה לממוצע</h3>
          <div className="patterns-grid">
            {filteredPatterns.meanReversion.slice(0, 8).map((opp, index) => (
              <div key={index} className={`pattern-card ${opp.direction}`}>
                <div className="pattern-icon">
                  {opp.direction === 'oversold' ? '📉' : '📈'}
                </div>
                <div className="pattern-content">
                  <div className="pattern-title">
                    {opp.direction === 'oversold' ? 'Oversold' : 'Overbought'}
                  </div>
                  <div className="pattern-detail">
                    Z-Score: {opp.zScore}
                  </div>
                  <div className="pattern-detail">
                    מחיר: {opp.currentPrice} | ממוצע: {opp.mean}
                  </div>
                  <div className="pattern-dates">{opp.date}</div>
                  <div className={`pattern-signal ${opp.signal}`}>
                    {opp.signal === 'strong' ? '⚠️ סיגנל חזק' : '👀 סיגנל בינוני'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breakouts */}
      {(selectedPattern === 'all' || selectedPattern === 'breakouts') && 
       filteredPatterns.breakouts.length > 0 && (
        <div className="pattern-section">
          <h3>🚀 פריצות (Breakouts)</h3>
          <div className="patterns-grid">
            {filteredPatterns.breakouts.slice(0, 8).map((breakout, index) => (
              <div key={index} className={`pattern-card ${breakout.direction}`}>
                <div className="pattern-icon">
                  {breakout.direction === 'upside' ? '🚀' : '💥'}
                </div>
                <div className="pattern-content">
                  <div className="pattern-title">
                    פריצה {breakout.direction === 'upside' ? 'כלפי מעלה' : 'כלפי מטה'}
                  </div>
                  <div className="pattern-detail">
                    רמה: {breakout.breakoutLevel}
                  </div>
                  <div className="pattern-detail">
                    מחיר נוכחי: {breakout.currentPrice}
                  </div>
                  <div className="pattern-detail magnitude">
                    גודל: {breakout.magnitude}
                  </div>
                  <div className="pattern-dates">{breakout.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PatternsTab;
